import React from "react";
import { Alert, Snackbar, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import VendorsGrid from "../components/vendors/VendorsGrid";
import { VendorDTO } from "../types/VendorDTO";
import AddVendorModal from "../components/vendors/AddVendorModal";
import { getVendors } from "../api/vendors";
import { useAppTranslation } from "../hooks/useAppTranslation";
import { buildGridStateStorageKey } from "../utils/gridStatePersistence";

type AddVendorConfirmPayload = {
  name: string;
  nip?: string;
  residenceAddress?: string;
  workingAddress?: string;
  address?: string;
  email?: string;
  countryCode?: string;
  bankAccountNo?: string;
  iban?: string;
  currencyCode?: string;
  defaultBankAccount?: boolean;
  city?: string;
  postCode?: string;
};

type Props = {
  companyId: string;
};

export default function VendorsPage({ companyId }: Props) {
  const { t } = useAppTranslation("common");
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<VendorDTO[]>([]);
  const [addOpen, setAddOpen] = React.useState(false);
  const [addSubmitting, setAddSubmitting] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);
  const [successOpen, setSuccessOpen] = React.useState(false);

  const gridStorageKey = React.useMemo(
    () =>
      buildGridStateStorageKey({
        table: "vendors",
        companyId,
      }),
    [companyId]
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVendors(companyId);
      setRows(res);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleOpenAddVendor = () => {
    setAddError(null);
    setAddOpen(true);
  };

  const handleCloseAddVendor = () => {
    if (addSubmitting) return;
    setAddOpen(false);
    setAddError(null);
  };

  const handleConfirmAddVendor = async (mf: AddVendorConfirmPayload) => {
    setAddSubmitting(true);
    setAddError(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/vendors?companyId=${companyId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: mf.name,
            nip: mf.nip ?? null,
            address:
              mf.address ??
              mf.residenceAddress ??
              mf.workingAddress ??
              null,
            workingAddress: mf.workingAddress ?? null,
            email: mf.email ?? null,
            countryCode: mf.countryCode ?? null,
            city: mf.city ?? null,
            postCode: mf.postCode ?? null,
            bankAccountNo: mf.bankAccountNo ?? undefined,
            iban: mf.iban ?? undefined,
            currencyCode: mf.currencyCode === "PLN" ? "" : mf.currencyCode,
            defaultBankAccount: mf.defaultBankAccount,
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(
          errorData?.detail || t("Nie udało się dodać dostawcy")
        );
      }

      setAddOpen(false);
      setSuccessOpen(true);
      await load();
    } catch (e: any) {
      setAddError(e.message || t("Nie udało się dodać dostawcy"));
    } finally {
      setAddSubmitting(false);
    }
  };

  return (
    <>
      <Stack sx={{ flex: 1, minHeight: 0, p: 2 }}>
        <VendorsGrid
          gridStorageKey={gridStorageKey}
          rows={rows}
          loading={loading}
          onRowClick={(id) => navigate(`/vendors/${id}`)}
          onAddVendor={handleOpenAddVendor}
        />

        <AddVendorModal
          open={addOpen}
          onClose={handleCloseAddVendor}
          onConfirm={handleConfirmAddVendor}
          companyId={companyId}
          isSubmitting={addSubmitting}
          submitError={addError}
        />
      </Stack>

      <Snackbar
        open={successOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {t("Dostawca został dodany pomyślnie")}
        </Alert>
      </Snackbar>
    </>
  );
}